import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '../../components/common/EmptyState';
import { SkeletonTable } from '../../components/common/SkeletonTable';
import { apiClient } from '../../services/api';
import type { AssistantUsageRow, AssistantUsageSummary, SystemAiCreditsOverview } from '../../types';
import { exportToCsv } from '../../utils/exportTable';
import { formatCurrency, formatInteger } from '../../utils/formatters';

type PeriodDays = 7 | 30 | 90;
type AssistantUsageDisplayRow = AssistantUsageRow & {
  creditsUsed: number;
  creditsRemaining: number;
  monthlyCostCapUsd: number | null;
  totalCustomerCost: number;
};
type SortKey = keyof Pick<
  AssistantUsageDisplayRow,
  'tenantName' | 'promptTokens' | 'completionTokens' | 'totalTokens' | 'interactions' | 'estimatedCost' | 'creditsUsed' | 'creditsRemaining'
>;
type SortDirection = 'asc' | 'desc';

const PERIOD_OPTIONS: Array<{ label: string; value: PeriodDays }> = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

const INPUT_COST_PER_MILLION = 2.5;
const OUTPUT_COST_PER_MILLION = 10;

function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return 'Unexpected error';
}

function SummaryCardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-5">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`summary-skeleton-${index}`}
          className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="animate-pulse space-y-3">
            <div className="h-3 w-24 rounded bg-gray-300 dark:bg-gray-600" />
            <div className="h-7 w-36 rounded bg-gray-300 dark:bg-gray-600" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  return <span aria-hidden="true" className="ml-1 text-xs">{active ? (direction === 'asc' ? '▲' : '▼') : '↕'}</span>;
}

export function AssistantUsagePage() {
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [sortKey, setSortKey] = useState<SortKey>('totalTokens');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const usageQuery = useQuery<AssistantUsageSummary, Error>({
    queryKey: ['assistant-usage', period],
    queryFn: () => apiClient.getSystemAssistantUsage({ days: period }),
  });

  const systemAiCreditsQuery = useQuery<SystemAiCreditsOverview, Error>({
    queryKey: ['system-ai-credits', period],
    queryFn: () => apiClient.getSystemAiCreditsOverview(period),
  });

  const rowsWithCredits = useMemo<AssistantUsageDisplayRow[]>(() => {
    const usageRows = usageQuery.data?.rows ?? [];
    const tenantCreditMap = new Map(
      (systemAiCreditsQuery.data?.tenantBreakdown ?? []).map((tenant) => [tenant.tenantId, tenant]),
    );

    return usageRows.map((row) => {
      const tenantCreditSummary = tenantCreditMap.get(row.tenantId);
      return {
        ...row,
        creditsUsed: tenantCreditSummary?.creditsUsed ?? 0,
        creditsRemaining: tenantCreditSummary?.creditsRemaining ?? 0,
        monthlyCostCapUsd: tenantCreditSummary?.monthlyCostCapUsd ?? null,
        totalCustomerCost: tenantCreditSummary?.totalCustomerCost ?? 0,
      };
    });
  }, [systemAiCreditsQuery.data?.tenantBreakdown, usageQuery.data?.rows]);

  const sortedRows = useMemo(() => {
    const rowsToSort = rowsWithCredits;
    const sorted = [...rowsToSort].sort((a, b) => {
      if (sortKey === 'tenantName') {
        const compareResult = (a.tenantName ?? a.tenantId).localeCompare(b.tenantName ?? b.tenantId);
        return sortDirection === 'asc' ? compareResult : -compareResult;
      }

      const compareResult = a[sortKey] - b[sortKey];
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });

    return sorted;
  }, [rowsWithCredits, sortDirection, sortKey]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection('desc');
  };

  const handleExportCsv = () => {
    exportToCsv(sortedRows, [
      { header: 'Tenant Name', accessor: 'tenantName' },
      { header: 'Prompt Tokens', accessor: 'promptTokens' },
      { header: 'Completion Tokens', accessor: 'completionTokens' },
      { header: 'Total Tokens', accessor: 'totalTokens' },
      { header: 'Interactions', accessor: 'interactions' },
      { header: 'Est. Cost', accessor: (row) => row.estimatedCost.toFixed(4) },
      { header: 'Credits Used', accessor: 'creditsUsed' },
      { header: 'Credits Remaining', accessor: 'creditsRemaining' },
      {
        header: 'Monthly Cost Cap (USD)',
        accessor: (row) => (row.monthlyCostCapUsd === null ? 'Unlimited' : String(row.monthlyCostCapUsd)),
      },
    ], `assistant-usage-${period}-days`);
  };

  if (usageQuery.isError) {
    return (
      <div>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold">AI Assistant Usage</h1>
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            onClick={() => usageQuery.refetch()}
          >
            Retry
          </button>
        </div>
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <p className="font-medium">Failed to load assistant usage data.</p>
          <p className="mt-1 break-all">Reason: {getErrorMessage(usageQuery.error)}</p>
          <p className="mt-1">Endpoint: /api/system-monitoring/assistant-usage?days={period}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">AI Assistant Usage</h1>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:hover:bg-gray-700"
          onClick={() => usageQuery.refetch()}
          disabled={usageQuery.isFetching}
          aria-label="Refresh AI assistant usage data"
        >
          {usageQuery.isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div
        className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1 dark:border-gray-700 dark:bg-gray-800"
        role="group"
        aria-label="Usage period filter"
      >
        {PERIOD_OPTIONS.map((option) => {
          const isActive = period === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
              }`}
              onClick={() => setPeriod(option.value)}
              aria-pressed={isActive}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <p className="mb-6 text-xs text-gray-600 dark:text-gray-400">
        Cost rates: input {formatCurrency(INPUT_COST_PER_MILLION)} / 1M tokens, output{' '}
        {formatCurrency(OUTPUT_COST_PER_MILLION)} / 1M tokens.
      </p>

      {usageQuery.isFetching && !usageQuery.isPending && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
          Refreshing assistant usage data...
        </div>
      )}

      {usageQuery.isPending ? (
        <SummaryCardsSkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-5">
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Total Prompt Tokens</p>
            <p className="mt-2 text-2xl font-bold text-gray-100">{formatInteger(usageQuery.data?.totalPromptTokens ?? 0)}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Total Completion Tokens</p>
            <p className="mt-2 text-2xl font-bold text-gray-100">{formatInteger(usageQuery.data?.totalCompletionTokens ?? 0)}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Total Tokens</p>
            <p className="mt-2 text-2xl font-bold text-gray-100">{formatInteger(usageQuery.data?.totalTokens ?? 0)}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Total Interactions</p>
            <p className="mt-2 text-2xl font-bold text-gray-100">{formatInteger(usageQuery.data?.totalInteractions ?? 0)}</p>
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Estimated Cost</p>
            <p className="mt-2 text-2xl font-bold text-gray-100">{formatCurrency(usageQuery.data?.totalEstimatedCost ?? 0)}</p>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h2 className="text-lg font-semibold">AI Credits Summary</h2>
        {systemAiCreditsQuery.isPending ? (
          <div className="mt-3">
            <SkeletonTable rows={1} columns={3} />
          </div>
        ) : systemAiCreditsQuery.isError ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            Failed to load AI credits summary: {getErrorMessage(systemAiCreditsQuery.error)}
          </div>
        ) : (
          <>
            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Total Credits Used</p>
                <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatInteger(systemAiCreditsQuery.data?.totalCreditsUsedAllTenants ?? 0)}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Total Tokens</p>
                <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatInteger(systemAiCreditsQuery.data?.totalTokensAllTenants ?? 0)}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Estimated Cost</p>
                <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(systemAiCreditsQuery.data?.totalEstimatedCost ?? 0)}
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Period: last {period} days.
            </p>

            <div className="mt-4 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Tenant</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Credits Used</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Credits Remaining</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">Monthly Cap</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(systemAiCreditsQuery.data?.tenantBreakdown ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        No per-tenant AI credit usage data available for this period.
                      </td>
                    </tr>
                  ) : (
                    (systemAiCreditsQuery.data?.tenantBreakdown ?? []).map((tenant) => (
                      <tr key={tenant.tenantId}>
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{tenant.tenantName}</td>
                        <td className="px-3 py-2">{formatInteger(tenant.creditsUsed)}</td>
                        <td className="px-3 py-2">
                          {tenant.remainingCostBudgetUsd === null ? 'Unlimited' : formatCurrency(tenant.remainingCostBudgetUsd)}
                        </td>
                        <td className="px-3 py-2">
                          {tenant.monthlyCostCapUsd === null ? 'Unlimited' : formatCurrency(tenant.monthlyCostCapUsd)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h2 className="text-lg font-semibold">Per-Tenant Breakdown</h2>
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:hover:bg-gray-700"
            disabled={sortedRows.length === 0 || usageQuery.isPending}
            onClick={handleExportCsv}
          >
            Export CSV
          </button>
        </div>

        {usageQuery.isPending ? (
          <div className="p-4">
            <SkeletonTable rows={6} columns={9} />
          </div>
        ) : sortedRows.length === 0 ? (
          <EmptyState
            title="No usage data available"
            message={`No AI assistant usage was recorded for the last ${period} days.`}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                <tr>
                  {[
                    { key: 'tenantName', label: 'Tenant Name' },
                    { key: 'promptTokens', label: 'Prompt Tokens' },
                    { key: 'completionTokens', label: 'Completion Tokens' },
                    { key: 'totalTokens', label: 'Total Tokens' },
                    { key: 'interactions', label: 'Interactions' },
                    { key: 'estimatedCost', label: 'Est. Cost' },
                    { key: 'creditsUsed', label: 'Credits Used' },
                    { key: 'creditsRemaining', label: 'Credits Remaining' },
                    { key: 'monthlyCostCapUsd', label: 'Monthly Cost Cap (USD)' },
                  ].map((column) => (
                    <th
                      key={column.key}
                      className="px-4 py-3 font-semibold"
                      aria-sort={
                        sortKey === column.key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'
                      }
                    >
                      <button
                        type="button"
                        className="inline-flex items-center rounded hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-400"
                        onClick={() => {
                          if (column.key === 'monthlyCostCapUsd') {
                            return;
                          }
                          handleSort(column.key as SortKey);
                        }}
                      >
                        {column.label}
                        {column.key !== 'monthlyCostCapUsd' && <SortIcon active={sortKey === column.key} direction={sortDirection} />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedRows.map((row) => (
                  <tr key={row.tenantId} className="hover:bg-gray-50 dark:hover:bg-gray-800/70">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{row.tenantName}</td>
                    <td className="px-4 py-3">{formatInteger(row.promptTokens)}</td>
                    <td className="px-4 py-3">{formatInteger(row.completionTokens)}</td>
                    <td className="px-4 py-3 font-semibold">{formatInteger(row.totalTokens)}</td>
                    <td className="px-4 py-3">{formatInteger(row.interactions)}</td>
                    <td className="px-4 py-3">{formatCurrency(row.estimatedCost)}</td>
                    <td className="px-4 py-3">{formatInteger(row.creditsUsed)}</td>
                    <td className="px-4 py-3">{row.monthlyCostCapUsd === null ? 'Unlimited' : formatInteger(row.creditsRemaining)}</td>
                    <td className="px-4 py-3">{row.monthlyCostCapUsd === null ? 'Unlimited' : formatCurrency(row.monthlyCostCapUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
